/**
 * SPDX-FileCopyrightText: 2018 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import axios from '@nextcloud/axios'
import { generateOcsUrl, generateUrl } from '@nextcloud/router'

export class CardApi {

	url(url) {
		url = `/apps/deck${url}`
		return generateUrl(url)
	}

	ocsUrl(url) {
		url = `/apps/deck/api/v1.0${url}`
		return generateOcsUrl(url)
	}

	addCard(card) {
		return axios.post(this.ocsUrl('/cards'), card)
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	cloneCard(cardId, targetStackId) {
		return axios.post(generateOcsUrl(`apps/deck/api/v1.0/cards/${cardId}/clone`), {
			targetStackId,
		})
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	deleteCard(cardId) {
		return axios.delete(this.url(`/cards/${cardId}`))
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	deletedCards(boardId) {
		return axios.get(this.url(`/${boardId}/cards/deleted`))
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	updateCard(card, boardId) {
		return axios.put(this.ocsUrl(`/cards/${card.id}`), { ...card, boardId })
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	reorderCard(card, boardId) {
		return axios.put(this.ocsUrl(`/cards/${card.id}/reorder`), { ...card, boardId: boardId ?? null })
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	assignUser(cardId, id, type, boardId) {
		return axios.post(this.ocsUrl(`/cards/${cardId}/assign`), { userId: id, type, boardId })
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	removeUser(cardId, id, type, boardId) {
		return axios.put(this.ocsUrl(`/cards/${cardId}/unassign`), { userId: id, type, boardId })
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	archiveCard(card) {
		return axios.put(this.url(`/cards/${card.id}/archive`))
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	unArchiveCard(card) {
		return axios.put(this.url(`/cards/${card.id}/unarchive`))
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	markCardAsDone(card) {
		return axios.put(this.url(`/cards/${card.id}/done`))
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	markCardAsUndone(card) {
		return axios.put(this.url(`/cards/${card.id}/undone`))
			.then(
				(response) => {
					return Promise.resolve(response.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	assignLabelToCard(data) {
		return axios.post(this.ocsUrl(`/cards/${data.card.id}/label/${data.labelId}`), { boardId: data.boardId ? data.boardId : null })
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	removeLabelFromCard(data) {
		return axios.delete(this.ocsUrl(`/cards/${data.card.id}/label/${data.labelId}`), {
			data: {
				boardId: data.boardId ? data.boardId : null,
			},
		})
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	assignDependentCard(cardId, dependentCardId, boardId) {
		return axios.post(this.ocsUrl(`/cards/${cardId}/dependentCards/${dependentCardId}`), { boardId: boardId ?? null })
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	removeDependentCard(cardId, dependentCardId, boardId) {
		return axios.delete(this.ocsUrl(`/cards/${cardId}/dependentCards/${dependentCardId}`), {
			data: {
				boardId: boardId ?? null,
			},
		})
			.then(
				(response) => {
					return Promise.resolve(response.data.ocs.data)
				},
				(err) => {
					return Promise.reject(err)
				},
			)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

}
